import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, UrlTree} from '@angular/router';
import {IdService} from '../id/id.service';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {JwtHelperService} from '@auth0/angular-jwt';
import {PermissionsService} from "../permissions/permissions.service";
import {SharedService} from "../shared/shared.service";
import * as Cookies from 'js-cookie';
import {environment} from "../../../environments/environment";
import Constants from '../../shared/constants';

interface UserData {
    email: string;
    firstName: string;
    lastName: string;
    uuid: string;
    token: string;
}


interface DomainMappingResponse {
    data?: {
        mapping_status: 'contact_support' | 'join' | 'create';
        org_data?: any;
    };
}

@Injectable({
    providedIn: 'root'
})
export class UserDetailsGuard implements CanActivate {
    private readonly VALID_MAPPING_STATUSES = ['contact_support', 'join', 'create'];

    constructor(
        private router: Router,
        private idService: IdService,
        private jwtHelper: JwtHelperService,
        private featureFlagService: PermissionsService,
        private sharedService: SharedService
    ) {
    }

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        return this.handleLanguageDataCheck().pipe(
            switchMap(languageResult => {
                if (languageResult) {
                    return of(true);
                }

                const validationResult = this.validateInputParams(route);
                if (!validationResult.isValid) {
                    window.location.href = '/login';
                    return of(false);
                }

                // Check if required fields exist
                if (!validationResult.token || !validationResult.email) {
                    console.error('Missing required validation fields');
                    window.location.href = '/login';
                    return of(false);
                }

                const previousUrl = localStorage.getItem('previousUrl');
                const isComingFromJoinTeam = previousUrl?.includes('/signup/join-team');

                return this.idService.getIdUser(validationResult.token).pipe(
                    switchMap((response: any) => {
                        if (!response.data) {
                            throw new Error('Invalid response from ID service');
                        }

                        // If user doesn't exist, allow navigation to user-details
                        if (!response.data.exists) {
                            return of(true);
                        }

                        const userData: UserData = {
                            email: validationResult.email,
                            firstName: response.data.first_name,
                            lastName: response.data.last_name,
                            uuid: response.data.id_user,
                            token: validationResult.token
                        };

                        // Handle flow based on previous URL
                        if (isComingFromJoinTeam) {
                            return this.handleJoinTeamFlow(userData);
                        }

                        return this.handleNormalFlow(userData);
                    }),
                    catchError(this.handleError.bind(this))
                );
            }),
            catchError(this.handleError.bind(this))
        );
    }

    private validateInputParams(route: ActivatedRouteSnapshot): { isValid: boolean; token?: string; email?: string } {
        try {
            const urlParams = new URL(window.location.href);
            const token = urlParams.searchParams.get('t');
            const email = route.params['email'];

            if (!token) {
                console.warn('Token is missing from URL parameters');
                return {isValid: false};
            }

            if (!email) {
                console.warn('Email is missing from route parameters');
                return {isValid: false};
            }

            return {isValid: true, token, email};
        } catch (error) {
            console.error('Error validating input parameters:', error);
            return {isValid: false};
        }
    }

    private handleLanguageDataCheck(): Observable<boolean> {
        try {
            const languageData = this.sharedService.getLanguageData();
            if (languageData === true) {
                this.sharedService.setLanguageData(false);
                return of(true);
            }
            return of(false);
        } catch (error) {
            console.error('Error checking language data:', error);
            return of(false);
        }
    }

    private checkUserAuthentication(userData: UserData): {
        isAuthenticated: boolean;
        loggedInUserUuid?: string;
    } {
        try {
            if (!this.idService.loggedIn()) {
                return {isAuthenticated: false};
            }
            const authToken = this.idService.getAuthToken();
            if (!authToken) return {isAuthenticated: false};
            const loggedInUserUuid = this.jwtHelper.decodeToken(authToken as string).user_uuid;
            if (!loggedInUserUuid || loggedInUserUuid !== userData.uuid) return {isAuthenticated: false};
            return {isAuthenticated: true, loggedInUserUuid};
        } catch (error) {
            console.error('Error checking user authentication:', error);
            return {isAuthenticated: false};
        }
    }

    private handleJoinTeamFlow(userData: UserData): Observable<UrlTree> {
        const authStatus = this.checkUserAuthentication(userData);

        localStorage.removeItem('previousUrl');
        localStorage.setItem('signupUserData', JSON.stringify({...userData, _expires: Date.now() + 30000}));

        if (!authStatus.isAuthenticated) {
            return of(this.router.createUrlTree(['/user-verify']));
        }

        return of(this.router.createUrlTree(['/user-logged-in']));
    }

    private handleNormalFlow(userData: UserData): Observable<UrlTree> {
        const authStatus = this.checkUserAuthentication(userData);

        localStorage.removeItem('previousUrl');
        localStorage.setItem('signupUserData', JSON.stringify({...userData, _expires: Date.now() + 30000}));

        if (!authStatus.isAuthenticated) {
            return of(this.router.createUrlTree(['/user-verify']));
        }

        return of(this.router.createUrlTree(['/user-logged-in']));
    }

    private handleError(error: any): Observable<boolean> {
        console.error('Error in guard:', error);

        if (error.status === 401) {
            window.location.href = '/signup/expired';
            return of(false);
        }

        try {
            const locale = Cookies.get(Constants.LOCALE_TOKEN);
            const omniLocales = Constants.OMNI_LOCALES;
            let returnUrl: string;

            if (locale && omniLocales.includes(locale)) {
                returnUrl = `${environment.SIGNUP_URL}${locale.toUpperCase()}/error`;
            } else {
                returnUrl = `${environment.SIGNUP_URL}EN-AU/error`;
            }
            window.location.href = returnUrl;
        } catch (err) {
            console.error('Error constructing error URL:', err);
            window.location.href = '/error';
        }

        return of(false);
    }
}
